import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditAccountSelectField } from '../../../EditAccountSelectField';
import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import {
  type AccountSummaryData,
  GET_ACCOUNT_SUMMARY
} from '../queries/accountSummaryQuery';

// Convert ISO date string to YYYY-MM-DD format for input
const toDateInputValue = (isoDate: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
};

interface FieldMapping {
  apiField: string;
  formField: keyof AccountSummaryData;
  isDate?: boolean;
}

const ACCOUNT_FIELDS: FieldMapping[] = [
  { apiField: 'alternateAccountName1', formField: 'alternativeAccountName1' },
  { apiField: 'alternateAccountName2', formField: 'alternativeAccountName2' },
  { apiField: 'division', formField: 'mlDivision' },
  { apiField: 'mlIsPbig', formField: 'mlIsPbig' },
  { apiField: 'smPortfolioCompany', formField: 'starMountainPortfolioCompany' },
  { apiField: 'fundType', formField: 'fundType' },
  { apiField: 'placementAgent', formField: 'starMountainPlacementAgent' },
  {
    apiField: 'lpPlatformSuppressEmails',
    formField: 'lpPlatformSuppressEmails'
  },
  { apiField: 'convergency', formField: 'convergencyCollaboration' },
  { apiField: 'parentAccountId', formField: 'parentAccountId' },
  { apiField: 'yearFounded', formField: 'yearFounded' }
];

const MURANO_FIELDS: FieldMapping[] = [
  {
    apiField: 'muranoDateReceived',
    formField: 'muranoDateReceived',
    isDate: true
  },
  { apiField: 'muranoTeam', formField: 'muranoTeam' },
  { apiField: 'muranoApproved', formField: 'muranoApproved' },
  { apiField: 'muranoStatus', formField: 'muranoStatus' },
  { apiField: 'muranoIo', formField: 'muranoIo' },
  { apiField: 'muranoNotes', formField: 'muranoNotes' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountSummaryData,
  initial: AccountSummaryData
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    // For dates, compare normalized values
    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      // Convert empty strings to null for API
      if (currentVal === '') {
        changes[apiField] = null;
      } else if (isDate) {
        changes[apiField] = currentVal || null;
      } else if (apiField === 'yearFounded') {
        // Convert yearFounded string to number
        const numVal = Number(currentVal);
        changes[apiField] = isNaN(numVal) ? null : numVal;
      } else {
        changes[apiField] = currentVal;
      }
    }
  }

  return changes;
}

// Mutations
const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        name
        alternateAccountName1
        alternateAccountName2
        division
        mlIsPbig
        smPortfolioCompany
        fundType
        placementAgent
        lpPlatformSuppressEmails
        convergency
        parentAccountId {
          id
          name
        }
      }
    }
  }
`;

const UPSERT_MURANO_TRACKING = gql`
  mutation UpsertMuranoTracking(
    $accountId: ID!
    $values: MuranoTrackingUpsertInput!
  ) {
    upsertMuranoTracking(accountId: $accountId, values: $values) {
      ok
      created
      muranoTracking {
        id
        accountId {
          id
          name
        }
        muranoApproved
        muranoDateReceived
        muranoIo
        muranoStatus
        muranoTeam
        muranoNotes
      }
    }
  }
`;

export interface EditOthersDialogProps {
  accountId: string;
  initialValue: AccountSummaryData;
  isOpen: boolean;
  onChange: (data: AccountSummaryData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountSummaryData;
}

export function EditOthersDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditOthersDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const refetchQueries = [
    { query: GET_ACCOUNT_SUMMARY, variables: { id: accountId } }
  ];

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, { refetchQueries });
  const [upsertMuranoTracking] = useMutation(UPSERT_MURANO_TRACKING, {
    refetchQueries
  });

  const accountChanges = getChangedValues(ACCOUNT_FIELDS, value, initialValue);
  const muranoChanges = getChangedValues(MURANO_FIELDS, value, initialValue);

  const hasAccountChanges = Object.keys(accountChanges).length > 0;
  const hasMuranoChanges = Object.keys(muranoChanges).length > 0;
  const isDirty = hasAccountChanges || hasMuranoChanges;

  const updateField = <K extends keyof AccountSummaryData>(
    field: K,
    fieldValue: AccountSummaryData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const mutations: Promise<unknown>[] = [];

      if (hasAccountChanges) {
        mutations.push(
          updateAccount({
            variables: { id: accountId, values: accountChanges }
          })
        );
      }

      if (hasMuranoChanges) {
        mutations.push(
          upsertMuranoTracking({
            variables: { accountId, values: muranoChanges }
          })
        );
      }

      const results = await Promise.all(mutations);

      // Check for errors in results
      const hasError = results.some(result => {
        const data = (result as { data?: Record<string, { ok?: boolean }> })
          .data;
        return data && Object.values(data).some(v => v.ok === false);
      });

      if (hasError) {
        toast.error('Failed to save some changes');
      } else {
        toast.success('Changes saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Others"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditFormField
          label="Alternative Account Name 1"
          onChange={v => {
            updateField('alternativeAccountName1', v);
          }}
          value={value.alternativeAccountName1}
        />
        <EditFormField
          label="Alternative Account Name 2"
          onChange={v => {
            updateField('alternativeAccountName2', v);
          }}
          value={value.alternativeAccountName2}
        />
        <EditDynamicSelectField
          fieldName="division"
          label="ML Division"
          model="opstream.account"
          onChange={v => {
            updateField('mlDivision', v);
          }}
          value={value.mlDivision}
        />
        <EditAccountSelectField
          currentAccountId={accountId}
          label="Parent Account"
          onChange={(id, name) => {
            onChange({ ...value, parentAccount: name, parentAccountId: id });
          }}
          value={
            value.parentAccountId
              ? { id: value.parentAccountId, name: value.parentAccount }
              : null
          }
        />
        <EditDynamicSelectField
          fieldName="fund_type"
          label="Fund Type"
          model="opstream.account"
          onChange={v => {
            updateField('fundType', v);
          }}
          value={value.fundType}
        />
        <EditDynamicSelectField
          fieldName="sm_portfolio_company"
          label="Star Mountain Portfolio Company"
          model="opstream.account"
          onChange={v => {
            updateField('starMountainPortfolioCompany', v);
          }}
          value={value.starMountainPortfolioCompany}
        />
        <EditCheckboxField
          checked={value.mlIsPbig}
          label="ML: IS PBIG"
          onCheckedChange={checked => {
            updateField('mlIsPbig', checked);
          }}
        />
        <EditDynamicSelectField
          fieldName="placement_agent"
          label="Star Mountain Placement Agent"
          model="opstream.account"
          onChange={v => {
            updateField('starMountainPlacementAgent', v);
          }}
          value={value.starMountainPlacementAgent}
        />
        <EditCheckboxField
          checked={value.lpPlatformSuppressEmails}
          label="LP Platform Suppress Emails"
          onCheckedChange={checked => {
            updateField('lpPlatformSuppressEmails', checked);
          }}
        />
        <EditFormField
          label="Murano Date Received"
          onChange={v => {
            updateField('muranoDateReceived', v);
          }}
          placeholder="Select date"
          type="date"
          value={toDateInputValue(value.muranoDateReceived)}
        />
        <EditCheckboxField
          checked={value.convergencyCollaboration}
          label="Convergency Collaboration"
          onCheckedChange={checked => {
            updateField('convergencyCollaboration', checked);
          }}
        />
        <EditFormField
          label="Year Founded"
          onChange={v => {
            updateField('yearFounded', v);
          }}
          type="number"
          value={value.yearFounded}
        />
        <EditDynamicSelectField
          fieldName="murano_team"
          label="Murano Team"
          model="opstream.murano.tracking"
          onChange={v => {
            updateField('muranoTeam', v);
          }}
          value={value.muranoTeam}
        />
        <EditCheckboxField
          checked={value.muranoApproved}
          label="Murano Approved"
          onCheckedChange={checked => {
            updateField('muranoApproved', checked);
          }}
        />
        <EditDynamicSelectField
          fieldName="murano_status"
          label="Murano Status"
          model="opstream.murano.tracking"
          onChange={v => {
            updateField('muranoStatus', v);
          }}
          value={value.muranoStatus}
        />
        <EditCheckboxField
          checked={value.muranoIo}
          label="Murano IO"
          onCheckedChange={checked => {
            updateField('muranoIo', checked);
          }}
        />
      </div>
      <div className="mt-4">
        <EditTextareaField
          label="Murano Notes"
          onChange={v => {
            updateField('muranoNotes', v);
          }}
          value={value.muranoNotes}
        />
      </div>
    </EditDialog>
  );
}
